// Constants
import {REGEX} from '@app/constants'

// Hooks
import {useIntl} from 'react-intl'
import {useToast} from '@app/hooks/use-toast'
import {useXSSProtection} from '@app/hooks/use-xss-protection'
import useShipToCountry from '@app/hooks/use-ship-to-country'

/**
 * Custom hook to generate form fields for the contact form of the Bespoke page.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.form - The form object containing control, formState, and setValue.
 * @param {Object} params.form.control - Control object for form management.
 * @param {Object} params.form.formState - Object containing form state, including errors.
 * @param {Object} params.form.formState.errors - Object containing form field errors.
 * @param {Function} params.form.setValue - Function to set form field values.
 * @param {string} [params.prefix='bespoke_'] - A prefix to be used for field names.
 *
 * @returns {Object} An object containing configuration for each form field.
 */
export function useBespokeContactFields({
    form: {
        control,
        formState: {errors},
        setValue,
        clearErrors,
        watch
    },
    prefix = 'bespoke_'
}) {
    const {shipToCountry} = useShipToCountry()
    const {validateField} = useXSSProtection()
    const {formatMessage} = useIntl()
    const toast = useToast()

    const formName = prefix + 'bespoke-contact'

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'bespoke_contact.field.first_name.label'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'bespoke_contact.field.first_name.error.required'
                }),
                validate: (value) => validateField(toast, value, formName, 'firstName')
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'bespoke_contact.field.last_name.label'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'bespoke_contact.field.last_name.error.required'
                }),
                validate: (value) => validateField(toast, value, formName, 'lastName')
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'bespoke_contact.field.email.label'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'bespoke_contact.field.email.error.required'
                }),
                validate: (value) => validateField(toast, value, formName, 'email'),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'bespoke_contact.field.email.error.invalid_email'
                    })
                }
            },
            error: errors[`${prefix}email`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone',
                id: 'bespoke_contact.field.phone.label'
            }),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            error: errors[`${prefix}phone`],
            setValue,
            clearErrors,
            control,
            defaultCountry: shipToCountry?.id
        },
        project: {
            name: `${prefix}project`,
            label: formatMessage({
                defaultMessage: 'Your Project',
                id: 'bespoke_contact.field.project.label'
            }),
            defaultValue: '',
            type: 'select',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select a value.',
                    id: 'bespoke_contact.field.project.error.required'
                })
            },
            options: [
                {
                    value: '',
                    label: formatMessage({
                        id: 'bespoke_contact.field.project.topic.default',
                        defaultMessage: 'Select a topic'
                    })
                },
                {
                    value: 'hospitality',
                    label: formatMessage({
                        id: 'bespoke_contact.field.project.topic.hospitality',
                        defaultMessage: 'Hospitality'
                    })
                },
                {
                    value: 'corporate-gifting',
                    label: formatMessage({
                        id: 'bespoke_contact.field.project.topic.corporate_gifting',
                        defaultMessage: 'Corporate gifting'
                    })
                },
                {
                    value: 'made-to-measure',
                    label: formatMessage({
                        id: 'bespoke_contact.field.project.topic.made_to_measure',
                        defaultMessage: 'Made to measure'
                    })
                }
            ],
            error: errors[`${prefix}project`],
            control
        },
        companyName: {
            name: `${prefix}companyName`,
            label: formatMessage({
                defaultMessage: 'Company Name',
                id: 'bespoke_contact.field.companyName.label'
            }),
            rules: {
                validate: (value) => validateField(toast, value, formName, 'companyName')
            },
            type: 'text',
            autoComplete: 'organization',
            defaultValue: '',
            error: errors[`${prefix}companyName`],
            control
        },
        comments: {
            control,
            name: `${prefix}comments`,
            type: 'textarea',
            label: formatMessage({
                defaultMessage: 'Comments',
                id: 'bespoke_contact.field.comments.label'
            }),
            defaultValue: '',
            error: errors?.[`${prefix}comments`],
            helpText: formatMessage(
                {
                    defaultMessage:
                        'You have {remainingCharacters} characters left out of {maxLength}.',
                    id: 'bespoke_contact.field.comments.help_text'
                },
                {
                    remainingCharacters: 500 - (watch(`${prefix}comments`)?.length ?? 0),
                    maxLength: 500
                }
            ),
            rules: {
                maxLength: {
                    value: 500,
                    message: formatMessage(
                        {
                            defaultMessage: 'Please limit your message to {maxLength} characters.',
                            id: 'bespoke_contact.field.comments.error.max_length'
                        },
                        {maxLength: 500}
                    )
                },
                validate: (value) => validateField(toast, value, formName, 'comments')
            }
        }
    }

    return fields
}
